import React from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  Icon,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType, FileType, artifactTypeFromJSON } from "build-ts/common";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { AppIcons } from "constants/icons";
import WorkflowRunCardContainer from "modules/artifacts/workflow/containers/WorkflowRunCard";

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Count output FileArtifacts by FileType across the unpopulated artifacts list. */
function countOutputFileTypes(artifact: ArtifactProto): { tables: number; charts: number; docs: number } {
  const outputs = artifact.workflow_run?.output_artifacts ?? [];
  let tables = 0;
  let charts = 0;
  let docs = 0;
  for (const a of outputs) {
    if (artifactTypeFromJSON(a.artifact_type) === ArtifactType.FILE_ARTIFACT) {
      const ft = a.file_artifact?.file_type;
      if (ft === FileType.CSV) tables++;
      else if (ft === FileType.CHART_SPEC_JSON) charts++;
      else if (ft === FileType.DOCX) docs++;
    }
  }
  return { tables, charts, docs };
}

// ── RunRow ────────────────────────────────────────────────────────────────────

interface RunRowProps {
  artifact: ArtifactProto;
  isExpanded: boolean;
  isLoadingPopulate: boolean;
  populatedArtifact: ArtifactProto | undefined;
  defaultTimeZone: string | undefined;
  onExpandToggle: (artifact: ArtifactProto) => void;
  /** Author display name resolved from the run's source_chat_id. */
  authorName?: string;
}

const RunRow: React.FC<RunRowProps> = ({
  artifact,
  isExpanded,
  isLoadingPopulate,
  populatedArtifact,
  defaultTimeZone,
  onExpandToggle,
  authorName,
}) => {
  const run = artifact.workflow_run;
  const description = run?.header?.description ?? run?.header?.name ?? "Workflow Run";
  const createdDate = run?.header?.created_date;
  const chatId = run?.source_chat_id;
  // Guard against empty/undefined timezone — ZoneId.of("") throws
  const safeTimeZone = defaultTimeZone || undefined;
  const timeLabel =
    createdDate !== undefined && safeTimeZone
      ? epochConverterWithTimeZone(Number(createdDate), "d MMM yyyy · HH:mm", safeTimeZone)
      : undefined;

  const { tables, charts, docs } = countOutputFileTypes(artifact);
  const hasPills = tables > 0 || charts > 0 || docs > 0;

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      overflow="hidden"
      bg="white"
      _hover={{ borderColor: "purple.300" }}
      transition="border-color 0.15s"
    >
      {/* ── Header row: description · date · chat · expand ── */}
      <Box
        px={4}
        pt={3}
        pb={hasPills ? 1.5 : 3}
        bg={isExpanded ? "purple.50" : "white"}
        _hover={{ bg: "purple.50" }}
        transition="background 0.12s"
      >
        <Flex align="center" gap={3}>
          {/* Description — left-aligned, takes remaining space */}
          <Text fontSize="sm" fontWeight="700" color="gray.800" noOfLines={2} m={0} flex={1} minW={0} textAlign="left">
            {description}
          </Text>

          {/* Right cluster: date · chat icon · expand */}
          <HStack spacing={1.5} flexShrink={0} align="center">
            {timeLabel && (
              <Text
                fontSize="xs"
                color="gray.500"
                fontWeight="500"
                m={0}
                sx={{ fontVariantNumeric: "tabular-nums" }}
                whiteSpace="nowrap"
              >
                {timeLabel}
              </Text>
            )}

            {chatId && (
              <Tooltip label="Open chat session" placement="top" hasArrow fontSize="xs">
                <Box
                  as="button"
                  type="button"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    window.open(`/chat/${chatId}`, "_blank");
                  }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  p={1}
                  borderRadius="md"
                  color="gray.500"
                  _hover={{ color: "purple.600", bg: "purple.100" }}
                  transition="all 0.15s"
                >
                  <Icon as={AppIcons.chatBubble} boxSize={4} />
                </Box>
              </Tooltip>
            )}

            {isLoadingPopulate ? (
              <Spinner size="sm" color="purple.600" thickness="2px" />
            ) : (
              <Box
                as="button"
                type="button"
                onClick={() => onExpandToggle(artifact)}
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={1}
                borderRadius="md"
                color="gray.500"
                _hover={{ color: "purple.600", bg: "purple.100" }}
                transition="all 0.15s"
              >
                <Icon as={isExpanded ? AppIcons.chevronUp : AppIcons.chevronDown} boxSize={3} />
              </Box>
            )}
          </HStack>
        </Flex>

        {/* ── Pills row (below description) ── */}
        <HStack justify="space-between" align="center">
          {hasPills && (
            <HStack spacing={1.5} mt={1.5} flexWrap="wrap">
              {tables > 0 && (
                <HStack
                  spacing={1}
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.300"
                  bg="white"
                >
                  <Icon as={AppIcons.dataframe} boxSize={3} color="gray.500" />
                  <Text fontSize="xs" color="gray.600" fontWeight="600" m={0}>
                    {tables} {tables === 1 ? "table" : "tables"}
                  </Text>
                </HStack>
              )}
              {charts > 0 && (
                <HStack
                  spacing={1}
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.300"
                  bg="white"
                >
                  <Icon as={AppIcons.chart} boxSize={3} color="gray.500" />
                  <Text fontSize="xs" color="gray.600" fontWeight="600" m={0}>
                    {charts} {charts === 1 ? "chart" : "charts"}
                  </Text>
                </HStack>
              )}
              {docs > 0 && (
                <HStack
                  spacing={1}
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.300"
                  bg="white"
                >
                  <Icon as={AppIcons.file} boxSize={3} color="gray.500" />
                  <Text fontSize="xs" color="gray.600" fontWeight="600" m={0}>
                    {docs} {docs === 1 ? "file" : "files"}
                  </Text>
                </HStack>
              )}
            </HStack>
          )}
          {/* ── Run by ── */}
          {authorName && (
            <HStack spacing={1} mt={hasPills ? 1 : 1.5}>
              <Icon as={AppIcons.user} boxSize={3} color="gray.400" />
              <Text fontSize="xs" color="gray.500" fontWeight="500" m={0}>
                {authorName}
              </Text>
            </HStack>
          )}
        </HStack>
      </Box>

      {/* ── Expanded content ── */}
      <Collapse in={isExpanded && !!populatedArtifact} animateOpacity>
        {populatedArtifact && (
          <Box borderTop="1px solid" borderColor="gray.100" minH="320px" maxH="600px" display="flex" flexDir="column">
            <WorkflowRunCardContainer
              artifact={populatedArtifact}
              hideHeader={true}
              defaultTimeZone={safeTimeZone ?? ""}
              authorName={authorName}
            />
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

// ── Props ─────────────────────────────────────────────────────────────────────

export interface WorkflowRunsOutputListProps {
  outputs: ArtifactProto[];
  totalOutputs: number;
  isLoadingOutputs: boolean;
  isLoadingMore: boolean;
  populatedRuns: Record<string, ArtifactProto>;
  expandedIds: Set<string>;
  loadingPopulateIds: Set<string>;
  defaultTimeZone: string | undefined;
  isSynthesisLoading: boolean;
  onExpandRun: (artifact: ArtifactProto) => void;
  onShowSynthesis: () => void;
  onLoadMore: () => void;
  /** Map of runId → author display name, resolved from source_chat_id. */
  runAuthorNames?: Record<string, string>;
}

// ── Main component ────────────────────────────────────────────────────────────

const WorkflowRunsOutputList: React.FC<WorkflowRunsOutputListProps> = ({
  outputs,
  totalOutputs,
  isLoadingOutputs,
  isLoadingMore,
  populatedRuns,
  expandedIds,
  loadingPopulateIds,
  defaultTimeZone,
  isSynthesisLoading,
  onExpandRun,
  onShowSynthesis,
  onLoadMore,
  runAuthorNames,
}) => {
  if (isLoadingOutputs) {
    return (
      <Flex justify="center" align="center" flex={1}>
        <Spinner size="md" color="purple.700" thickness="2px" />
      </Flex>
    );
  }

  if (outputs.length === 0) {
    return (
      <Alert status="info" bg="gray.50" border="1px" borderColor="purple.600" color="purple.700" fontWeight="600">
        <AlertIcon color="purple.700" />
        No workflow runs found for this workflow.
      </Alert>
    );
  }

  return (
    <Flex direction="column" flex={1} minH={0} gap={0}>
      {/* Count row + Show Synthesis button */}
      <Flex align="center" justify="space-between" px={1} mb={3} flexShrink={0}>
        <Text fontSize="sm" color="gray.500" fontWeight="600" m={0}>
          Showing {outputs.length}
          {totalOutputs > outputs.length ? ` of ${totalOutputs}` : ""} {totalOutputs === 1 ? "run" : "runs"}
        </Text>
        <Button
          size="sm"
          variant="outline"
          colorScheme="purple"
          borderRadius="lg"
          isLoading={isSynthesisLoading}
          loadingText="Loading..."
          onClick={onShowSynthesis}
        >
          Show synthesis
        </Button>
      </Flex>

      {/* Run cards */}
      <Box
        flex={1}
        minH={0}
        overflowY="auto"
        pr={1}
        css={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "var(--chakra-colors-purple-200)", borderRadius: "2px" },
        }}
      >
        <VStack spacing={2} align="stretch">
          {outputs.map((artifact, idx) => {
            const runId = artifact.workflow_run?.header?.id ?? String(idx);
            return (
              <RunRow
                key={runId}
                artifact={artifact}
                isExpanded={expandedIds.has(runId)}
                isLoadingPopulate={loadingPopulateIds.has(runId)}
                populatedArtifact={populatedRuns[runId]}
                defaultTimeZone={defaultTimeZone}
                onExpandToggle={onExpandRun}
                authorName={runAuthorNames?.[runId]}
              />
            );
          })}

          {outputs.length < totalOutputs && (
            <Button
              variant="outline"
              colorScheme="purple"
              size="sm"
              onClick={onLoadMore}
              isLoading={isLoadingMore}
              loadingText="Loading more..."
              w="100%"
            >
              Load More
            </Button>
          )}
        </VStack>
      </Box>
    </Flex>
  );
};

export default WorkflowRunsOutputList;
